/**
 * AIAssistantPage — "AI Assistant" / Home → New Task entry point.
 *
 * This is a thin wrapper around RunDrawer's `variant="page"` mode: the same
 * execution engine used everywhere else in the app (mentions, file/tool
 * attachment, model selection, streaming), skinned as a full-page experience
 * instead of the floating slide-in drawer. There is no separate "compose"
 * UI here anymore — RunDrawer renders the heading + suggestion chips itself
 * while history is empty, then the real chat once a task starts.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  Search,
  FileText,
  BarChart3,
  CalendarClock,
  Mail,
  Presentation,
  CheckSquare,
  Sparkles,
} from 'lucide-react';
import AppShellV2 from './AppShellV2.tsx';
import RunDrawer from '../agentsWorkflows/RunDrawer.jsx';
import ChatHistoryRail from './ChatHistoryRail.tsx';
import { customAgentAPI, onboardingAPI } from '../../services/api.js';
import { useDomainAgentSelection } from '../../hooks/useDomainAgentSelection.ts';

const SUGGESTIONS = [
  { label: 'Research competitors', icon: Search },
  { label: 'Create a report', icon: FileText },
  { label: 'Analyze data', icon: BarChart3 },
  { label: 'Plan my week', icon: CalendarClock },
  { label: 'Monitor my inbox', icon: Mail },
  { label: 'Prepare a presentation', icon: Presentation },
  { label: 'Run tests', icon: CheckSquare },
  { label: 'Anything else...', icon: Sparkles, focusOnly: true },
];

export default function AIAssistantPage({ onLogout }: { onLogout?: () => void }) {
  const [runAgents, setRunAgents] = useState<any[]>([]);
  const [userDomains, setUserDomains] = useState<string[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  // Prompt handed over from the Home composer / suggestion cards.
  const location = useLocation();
  const handoff = (location.state as { prompt?: string; autoSend?: boolean } | null) || null;
  const activeSessionId = searchParams.get('session');
  // Same selection the Home panel uses (kept per conversation), so expanding to full screen keeps the agents.
  const domainSelection = useDomainAgentSelection(activeSessionId);
  // Bumped by RunDrawer's onSessionSaved after each autosave so the rail
  // re-fetches and shows the latest title/timestamp without polling.
  const [railRefreshKey, setRailRefreshKey] = useState(0);
  // Memoized — an inline arrow function here previously got a fresh
  // identity on every render, which (via RunDrawer's persistHistory
  // useCallback depending on it) retriggered RunDrawer's autosave effect
  // on every render, which called this, which bumped railRefreshKey, which
  // caused this very re-render: an infinite loop hammering the chat-session
  // save/list/folders endpoints. RunDrawer itself no longer depends on this
  // prop's identity either (reads it via a ref), so this is now belt-and-
  // suspenders, not the only fix.
  const handleSessionSaved = useCallback(() => setRailRefreshKey((k) => k + 1), []);
  const handleNewChat = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('session');
      return next;
    }, { replace: true });
  }, [setSearchParams]);
  const handleSelectSession = useCallback((id: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('session', id);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    if (!handoff?.prompt) return undefined;
    // RunDrawer listens for this event once mounted; wait for it, then clear the state so a
    // refresh doesn't re-send the prompt.
    const timer = setTimeout(() => {
      window.dispatchEvent(new CustomEvent('lyra:set-run-target', { detail: { prepopulateText: handoff.prompt, autoSend: !!handoff.autoSend } }));
      window.history.replaceState({}, document.title);
    }, 700);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    customAgentAPI.list().then((res: any) => {
      setRunAgents(Array.isArray(res) ? res : res?.agents || []);
    }).catch(() => setRunAgents([]));
    onboardingAPI.get().then(({ onboarding }: any) => {
      setUserDomains(onboarding?.domains || []);
    }).catch(() => {});
  }, []);

  // Default New Task to the user's own Lyran agent — previously this ran
  // through an unrelated generic fallback (executeGeneral) with a hardcoded
  // system prompt, completely disconnected from the real personal agent,
  // its skills, and its memory. Explicitly @mentioning a different agent
  // still overrides this (RunDrawer's send() checks mentions/picks first).
  const lyranAgent = runAgents.find((a) => a.isLyran) || null;

  // Quick-pick pills: this user's domain-tagged agents first; if none match
  // (the common case until people start tagging agents — see agentDomains.ts),
  // fall back to their most recently created agents so the row isn't empty.
  const agentSuggestions = (() => {
    const domainMatches = userDomains.length
      ? runAgents.filter((a) => userDomains.includes(a.category || 'general'))
      : [];
    const pool = domainMatches.length > 0 ? domainMatches : runAgents;
    return [...pool]
      .sort((a, b) => new Date(b.updatedAt || b.createdAt || 0).getTime() - new Date(a.updatedAt || a.createdAt || 0).getTime())
      .slice(0, 6);
  })();

  return (
    <AppShellV2 pageTitle="AI Assistant" backTo="/dashboard" onLogout={onLogout}>
      <div style={{ height: '100%', display: 'flex' }}>
        <ChatHistoryRail
          activeSessionId={activeSessionId}
          refreshKey={railRefreshKey}
          onNewChat={handleNewChat}
          onSelect={handleSelectSession}
        />
        <div style={{ flex: 1, minWidth: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <RunDrawer
            variant="page"
            target={lyranAgent}
            targetType="agent"
            gmailStatus={null}
            agents={runAgents}
            spaceId={null}
            suggestions={SUGGESTIONS}
            agentSuggestions={agentSuggestions}
            pageHeading="Tell me what you want to get done."
            pageSubheading="I'll plan it, use the right agents, and make it happen."
            onSessionSaved={handleSessionSaved}
            domainAgents={domainSelection.selectedAgents}
            availableDomainAgents={domainSelection.available}
            onAddDomainAgent={domainSelection.toggle}
            onRemoveDomainAgent={domainSelection.remove}
          />
        </div>
      </div>
    </AppShellV2>
  );
}
